// src/pages/CoverLetterPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  getCoverLetterEligibility,
  markCoverLetterGenerated,
  saveGeneratedCoverLetter,
  getUserGeneratedCoverLetters,
} from "../utils/transactions";
import {
  downloadCoverLetterAsTxt,
  downloadCoverLetterAsHtml,
  downloadCoverLetterAsWord,
  downloadCoverLetterAsPdf,
} from "../utils/generateCoverLetterDoc";
import servicesConfig from "../utils/servicesConfig";

const BACKEND  = process.env.REACT_APP_BACKEND_URL || "https://ats-app-795v.onrender.com";
const APP_NAME = servicesConfig.app.name;

// ── Helpers ──
function scoreColor(s) { return s >= 75 ? "#059669" : s >= 50 ? "#d97706" : "#dc2626"; }
function scoreBg(s)    { return s >= 75 ? "#ecfdf5" : s >= 50 ? "#fffbeb" : "#fef2f2"; }
function fmtDate(ts) {
  if (!ts) return "—";
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}
function daysLeft(isoStr) {
  if (!isoStr) return null;
  return Math.ceil((new Date(isoStr) - new Date()) / (1000 * 60 * 60 * 24));
}

export default function CoverLetterPage() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();

  // ── Page state ──
  // Steps: loading → select → form → generating → result
  //        loading → ineligible
  //        loading → history (tab)
  const [pageStep,    setPageStep]    = useState("loading");
  const [activeTab,   setActiveTab]   = useState("new");   // new | history

  // ── Data ──
  const [analyses,    setAnalyses]    = useState([]);
  const [savedLetters,setSavedLetters]= useState([]);
  const [eligibility, setEligibility] = useState(null);

  // ── Form state ──
  const [selectedId,  setSelectedId]  = useState(null);
  const [tone,        setTone]        = useState("professional");
  const [resumeText,  setResumeText]  = useState("");
  const [jdText,      setJdText]      = useState("");

  // ── Result state ──
  const [letter,      setLetter]      = useState(null);
  const [savedDocId,  setSavedDocId]  = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  // eslint-disable-next-line no-unused-vars
  const [copied,      setCopied]      = useState(false);

  const fullName  = `${userData?.firstName||""} ${userData?.lastName||""}`.trim();
  const userEmail = userData?.email || user?.email || "";

  // ── Auth + load ──
  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    loadAll();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAll() {
    setPageStep("loading");
    try {
      const [elig, analysesSnap, saved] = await Promise.all([
        getCoverLetterEligibility(user.uid),
        getDocs(collection(db, "users", user.uid, "analyses")),
        getUserGeneratedCoverLetters(user.uid),
      ]);

      setEligibility(elig);
      setSavedLetters(saved);

      const list = analysesSnap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setAnalyses(list);

      if (!elig.eligible) {
        // No eligibility but may have saved letters to view
        setPageStep("ineligible");
        return;
      }

      // Auto-select if only one analysis with saved text
      if (list.length === 1 && list[0].resumeText) {
        applyAnalysis(list[0]);
        setPageStep("form");
      } else {
        setPageStep(list.length > 0 ? "select" : "form");
      }
    } catch (err) {
      console.error(err);
      setPageStep("form");
    }
  }

  function applyAnalysis(analysis) {
    setSelectedId(analysis.id);
    setResumeText(analysis.resumeText    || "");
    setJdText(analysis.jobDescription    || "");
  }

  function selectAndProceed(analysis) {
    applyAnalysis(analysis);
    setPageStep("form");
  }

  // ── Generate ──
  async function handleGenerate() {
    if (!resumeText.trim()) { setError("Please paste your resume text."); return; }
    if (!jdText.trim())     { setError("Please paste the job description."); return; }
    setError("");
    setPageStep("generating");

    try {
      const res = await fetch(`${BACKEND}/generate-cover-letter`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          resumeText,
          jobDescription: jdText,
          tone,
          userInfo: { name: fullName, email: userEmail },
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || "Generation failed"); }
      const data = await res.json();
      setLetter(data);

      // Save to Firestore + mark used — in parallel
      setSaving(true);
      try {
        const [docId] = await Promise.all([
          saveGeneratedCoverLetter(user.uid, {
            letterData:    data,
            tone,
            transactionId: eligibility?.transactionId || null,
            planId:        eligibility?.planId         || "ats_basic",
          }),
          eligibility?.transactionId
            ? markCoverLetterGenerated(user.uid, eligibility.transactionId)
            : Promise.resolve(),
        ]);
        setSavedDocId(docId);
        // Refresh saved letters
        const updated = await getUserGeneratedCoverLetters(user.uid);
        setSavedLetters(updated);
      } catch (saveErr) {
        console.error("Save failed (non-fatal):", saveErr);
      }
      setSaving(false);
      setPageStep("result");
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
      setPageStep("form");
    }
  }

  // eslint-disable-next-line no-unused-vars
  function handleCopy() {
    if (!letter?.full_text) return;
    navigator.clipboard.writeText(letter.full_text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleReset() {
    setLetter(null);
    setSavedDocId(null);
    setSelectedId(null);
    setResumeText("");
    setJdText("");
    setError("");
    setPageStep(analyses.length > 0 ? "select" : "form");
  }

  // ── Styles ──
  const card = {
    background: "#fff", borderRadius: 16,
    border: "1px solid #e2e8f0", padding: "24px 28px",
    marginBottom: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  };
  const lbl = {
    fontSize: 11, fontWeight: 700, color: "#374151",
    textTransform: "uppercase", letterSpacing: "0.06em",
    display: "block", marginBottom: 10,
  };

  // ── Download buttons component ──
  function DownloadButtons({ letterData, compact = false }) {
    const pad = compact ? "6px 10px" : "8px 14px";
    const fz  = compact ? 11 : 12;
    const br  = compact ? 7 : 9;
    const btn = (label, bg, color, border, onClick) => (
      <button onClick={onClick} style={{ padding: pad, background: bg, color, border: border || "none", borderRadius: br, fontWeight: 600, fontSize: fz, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}>
        {label}
      </button>
    );
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {btn("📋 Copy",    "#fff",                                  "#374151", "1.5px solid #e2e8f0", () => { navigator.clipboard.writeText(letterData.full_text); })}
        {btn("↓ TXT",     "#fff",                                  "#374151", "1.5px solid #e2e8f0", () => downloadCoverLetterAsTxt(letterData, "cover-letter.txt"))}
        {btn("↓ HTML",    "#fff",                                  "#374151", "1.5px solid #e2e8f0", () => downloadCoverLetterAsHtml(letterData, "cover-letter.html", fullName, userEmail))}
        {btn("↓ Word",    "#fff",                                  "#374151", "1.5px solid #e2e8f0", () => downloadCoverLetterAsWord(letterData, "cover-letter.docx", fullName, userEmail))}
        {btn("↓ PDF",     "linear-gradient(135deg,#7c3aed,#6d28d9)","#fff",  null,                   () => downloadCoverLetterAsPdf(letterData, fullName, userEmail))}
      </div>
    );
  }

  // ── Letter preview component ──
  function LetterPreview({ letterData }) {
    return (
      <div style={{ ...card, fontFamily: "Georgia,'Times New Roman',serif" }}>
        <div style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: 14, marginBottom: 20, fontSize: 12, color: "#64748b", fontFamily: "Arial,sans-serif" }}>
          {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </div>
        <p style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 20, fontFamily: "Arial,sans-serif" }}>
          {letterData.salutation || "Dear Hiring Manager,"}
        </p>
        {[letterData.opening, letterData.body1, letterData.body2, letterData.closing].filter(Boolean).map((para, i) => (
          <p key={i} style={{ fontSize: 14, color: "#1a1a1a", lineHeight: 1.9, marginBottom: 18 }}>{para}</p>
        ))}
        <div style={{ marginTop: 28, fontFamily: "Arial,sans-serif" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 4 }}>{letterData.sign_off || "Sincerely,"}</div>
          <div style={{ fontSize: 14, color: "#374151" }}>{fullName}</div>
          {userEmail && <div style={{ fontSize: 13, color: "#64748b" }}>{userEmail}</div>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter',-apple-system,sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform:rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0;transform:translateY(10px); } to { opacity:1;transform:translateY(0); } }
        .ac-card:hover { border-color:#7c3aed!important; background:#fafaff!important; box-shadow:0 4px 16px rgba(124,58,237,0.1)!important; }
      `}</style>

      {/* ── Nav ── */}
      <nav style={{ background:"#fff", borderBottom:"1px solid #e2e8f0", padding:"0 5%", height:60, display:"flex", alignItems:"center", justifyContent:"space-between", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }} onClick={() => navigate("/dashboard")}>
          <div style={{ width:32, height:32, background:"#111827", borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{ color:"#fff", fontWeight:800, fontSize:15 }}>R</span>
          </div>
          <span style={{ fontWeight:700, fontSize:16, color:"#111827" }}>{APP_NAME}</span>
        </div>
        <button onClick={() => navigate("/dashboard")} style={{ background:"none", border:"1px solid #e2e8f0", borderRadius:8, padding:"7px 16px", fontSize:13, cursor:"pointer", color:"#374151" }}>
          ← Dashboard
        </button>
      </nav>

      <div style={{ maxWidth:820, margin:"0 auto", padding:"32px 20px" }}>

        {/* ── Header ── */}
        <div style={{ display:"flex", alignItems:"center", gap:14, marginBottom:28 }}>
          <div style={{ width:50, height:50, borderRadius:14, background:"linear-gradient(135deg,#7c3aed,#6d28d9)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26, boxShadow:"0 6px 16px rgba(124,58,237,0.25)", flexShrink:0 }}>✉️</div>
          <div>
            <h1 style={{ fontSize:24, fontWeight:800, color:"#0f172a", letterSpacing:"-0.02em", margin:0 }}>AI Cover Letter Generator</h1>
            <p style={{ fontSize:13, color:"#64748b", margin:"3px 0 0" }}>
              Select an analysis, pick a tone, get a tailored cover letter in 15 seconds
            </p>
          </div>
        </div>

        {/* ── Tabs: New / History ── */}
        <div style={{ display:"flex", background:"#f1f5f9", borderRadius:10, padding:4, gap:4, marginBottom:24, width:"fit-content" }}>
          {[
            { id:"new",     label:"✉️ Generate New" },
            { id:"history", label:`📁 Saved (${savedLetters.length})` },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ padding:"8px 20px", borderRadius:8, border:"none", fontSize:13, fontWeight:600, cursor:"pointer", transition:"all 0.15s", background: activeTab===t.id ? "#fff" : "transparent", color: activeTab===t.id ? "#0f172a" : "#64748b", boxShadow: activeTab===t.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ══════════════════════════════════
            HISTORY TAB
        ══════════════════════════════════ */}
        {activeTab === "history" && (
          <div>
            {savedLetters.length === 0 ? (
              <div style={{ ...card, textAlign:"center", padding:"56px" }}>
                <div style={{ fontSize:40, marginBottom:14 }}>📭</div>
                <h3 style={{ fontSize:16, fontWeight:700, color:"#0f172a", marginBottom:8 }}>No saved cover letters</h3>
                <p style={{ fontSize:13, color:"#64748b", marginBottom:20 }}>Generate your first cover letter and it will appear here for 30 days.</p>
                <button onClick={() => setActiveTab("new")}
                  style={{ padding:"10px 24px", background:"linear-gradient(135deg,#7c3aed,#6d28d9)", color:"#fff", border:"none", borderRadius:10, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  Generate Cover Letter →
                </button>
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                {savedLetters.map((doc, i) => {
                  const dl = daysLeft(doc.expiresAt);
                  return (
                    <div key={doc.id} style={{ ...card, marginBottom:0 }}>
                      {/* Header */}
                      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12, marginBottom:16, flexWrap:"wrap" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                          <div style={{ width:40, height:40, borderRadius:10, background:"#f5f3ff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, flexShrink:0 }}>✉️</div>
                          <div>
                            <div style={{ fontSize:14, fontWeight:700, color:"#0f172a" }}>
                              Cover Letter #{savedLetters.length - i}
                              <span style={{ marginLeft:8, fontSize:11, background: dl !== null && dl <= 7 ? "#fef2f2" : "#f5f3ff", color: dl !== null && dl <= 7 ? "#dc2626" : "#7c3aed", padding:"1px 8px", borderRadius:20, fontWeight:600 }}>
                                {dl === null ? "" : dl <= 0 ? "Expired" : `${dl}d left`}
                              </span>
                            </div>
                            <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                              {fmtDate(doc.createdAt)} · Tone: {doc.tone || "professional"}
                            </div>
                          </div>
                        </div>
                        <DownloadButtons letterData={doc.letterData} compact />
                      </div>

                      {/* Subject line */}
                      {doc.letterData?.subject && (
                        <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:8, padding:"8px 14px", marginBottom:14, fontSize:12 }}>
                          <span style={{ fontWeight:700, color:"#5b21b6" }}>📧 Subject: </span>
                          <span style={{ color:"#1e1b4b" }}>{doc.letterData.subject}</span>
                        </div>
                      )}

                      {/* Preview — collapsible */}
                      <details>
                        <summary style={{ fontSize:12, fontWeight:600, color:"#7c3aed", cursor:"pointer", marginBottom:8, listStyle:"none", display:"flex", alignItems:"center", gap:4 }}>
                          <span>▶ Preview letter</span>
                        </summary>
                        <LetterPreview letterData={doc.letterData} />
                      </details>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════
            NEW TAB — ALL GENERATION STEPS
        ══════════════════════════════════ */}
        {activeTab === "new" && (
          <>
            {/* LOADING */}
            {pageStep === "loading" && (
              <div style={{ ...card, textAlign:"center", padding:"60px" }}>
                <div style={{ width:40, height:40, borderRadius:"50%", border:"3px solid #e2e8f0", borderTopColor:"#7c3aed", animation:"spin 0.8s linear infinite", margin:"0 auto 16px" }} />
                <p style={{ color:"#64748b" }}>Loading your analyses...</p>
              </div>
            )}

            {/* INELIGIBLE */}
            {pageStep === "ineligible" && (
              <div style={{ ...card, textAlign:"center", padding:"56px" }}>
                <div style={{ fontSize:40, marginBottom:14 }}>🔒</div>
                <h2 style={{ fontSize:18, fontWeight:700, color:"#0f172a", marginBottom:8 }}>No cover letters remaining</h2>
                <p style={{ fontSize:14, color:"#64748b", maxWidth:380, margin:"0 auto 24px", lineHeight:1.7 }}>
                  You've used your cover letter generation. Purchase a new plan to generate more.
                </p>
                <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                  <button onClick={() => navigate("/dashboard")}
                    style={{ padding:"11px 24px", background:"linear-gradient(135deg,#7c3aed,#6d28d9)", color:"#fff", border:"none", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                    Buy New Plan →
                  </button>
                  {savedLetters.length > 0 && (
                    <button onClick={() => setActiveTab("history")}
                      style={{ padding:"11px 24px", background:"#fff", color:"#7c3aed", border:"1.5px solid #ddd6fe", borderRadius:10, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                      View Saved Letters ({savedLetters.length})
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* SELECT ANALYSIS */}
            {pageStep === "select" && (
              <>
                {/* Eligibility badge */}
                <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:"10px 16px", marginBottom:20, fontSize:13, color:"#5b21b6", display:"flex", alignItems:"center", gap:8 }}>
                  ✨ <strong>{eligibility?.remaining}</strong> cover letter{eligibility?.remaining !== 1 ? "s" : ""} remaining — select an analysis to pre-fill your resume and job description
                </div>

                <div style={card}>
                  <div style={{ fontSize:15, fontWeight:700, color:"#0f172a", marginBottom:4 }}>Choose an Analysis</div>
                  <p style={{ fontSize:13, color:"#64748b", marginBottom:20 }}>
                    Your resume text and job description will load automatically from the selected analysis.
                  </p>

                  <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                    {analyses.map(a => {
                      const sc      = a.ats_score || 0;
                      const hasBoth = !!a.resumeText && !!a.jobDescription;
                      return (
                        <div key={a.id} className="ac-card"
                          onClick={() => selectAndProceed(a)}
                          style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 18px", borderRadius:12, border:`1.5px solid ${selectedId===a.id?"#7c3aed":"#e2e8f0"}`, background: selectedId===a.id?"#f5f3ff":"#fff", cursor:"pointer", transition:"all 0.15s" }}>

                          {/* Score */}
                          <div style={{ width:54, height:54, borderRadius:12, background:scoreBg(sc), display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            <div style={{ fontSize:22, fontWeight:800, color:scoreColor(sc), lineHeight:1 }}>{sc}</div>
                            <div style={{ fontSize:9, color:scoreColor(sc), fontWeight:700, textTransform:"uppercase" }}>score</div>
                          </div>

                          {/* Info */}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:4, flexWrap:"wrap" }}>
                              <span style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{fmtDate(a.createdAt)}</span>
                              <span style={{ fontSize:11, background:"#eff6ff", color:"#2563eb", padding:"1px 8px", borderRadius:20, fontWeight:600 }}>{a.match_rate||0}% match</span>
                              <span style={{ fontSize:11, background:"#fef2f2", color:"#dc2626", padding:"1px 8px", borderRadius:20, fontWeight:600 }}>{a.missing_count||0} missing</span>
                              {!hasBoth && <span style={{ fontSize:11, background:"#fffbeb", color:"#d97706", padding:"1px 8px", borderRadius:20, fontWeight:600 }}>⚠️ No saved text</span>}
                            </div>
                            {a.summary && (
                              <div style={{ fontSize:12, color:"#64748b", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.summary}</div>
                            )}
                            {(a.missing_keywords||[]).length > 0 && (
                              <div style={{ display:"flex", gap:4, marginTop:4, flexWrap:"wrap" }}>
                                {(a.missing_keywords||[]).slice(0,4).map(k => (
                                  <span key={k} style={{ fontSize:10, background:"#fee2e2", color:"#dc2626", padding:"1px 7px", borderRadius:20 }}>{k}</span>
                                ))}
                                {(a.missing_keywords||[]).length > 4 && (
                                  <span style={{ fontSize:10, color:"#94a3b8" }}>+{(a.missing_keywords||[]).length-4} more</span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Radio */}
                          <div style={{ width:22, height:22, borderRadius:"50%", border:`2px solid ${selectedId===a.id?"#7c3aed":"#d1d5db"}`, background: selectedId===a.id?"#7c3aed":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            {selectedId===a.id && <span style={{ color:"#fff", fontSize:11, fontWeight:800 }}>✓</span>}
                          </div>
                        </div>
                      );
                    })}

                    {/* Manual entry option */}
                    <div className="ac-card"
                      onClick={() => { setSelectedId(null); setResumeText(""); setJdText(""); setPageStep("form"); }}
                      style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 18px", borderRadius:12, border:"1.5px dashed #e2e8f0", background:"#fafafa", cursor:"pointer", transition:"all 0.15s" }}>
                      <div style={{ width:54, height:54, borderRadius:12, background:"#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, flexShrink:0 }}>✏️</div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#374151" }}>Enter Manually</div>
                        <div style={{ fontSize:12, color:"#94a3b8" }}>Paste your own resume and job description</div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* FORM */}
            {pageStep === "form" && (
              <>
                {/* Top bar */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:10 }}>
                  <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:"8px 14px", fontSize:13, color:"#5b21b6" }}>
                    ✨ <strong>{eligibility?.remaining||1}</strong> cover letter{(eligibility?.remaining||1)!==1?"s":""} remaining
                  </div>
                  {analyses.length > 0 && (
                    <button onClick={() => { setSelectedId(null); setPageStep("select"); }}
                      style={{ background:"none", border:"1px solid #e2e8f0", borderRadius:8, padding:"7px 14px", fontSize:13, cursor:"pointer", color:"#374151" }}>
                      ← Back to analyses
                    </button>
                  )}
                </div>

                {/* If analysis was selected — show selection pill */}
                {selectedId && (
                  <div style={{ background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:10, padding:"10px 16px", marginBottom:16, fontSize:13, color:"#166534", display:"flex", alignItems:"center", gap:8 }}>
                    ✅ Analysis pre-loaded · Edit text below if needed
                    <button onClick={() => { setSelectedId(null); setResumeText(""); setJdText(""); }}
                      style={{ marginLeft:"auto", fontSize:11, background:"none", border:"none", color:"#64748b", cursor:"pointer" }}>
                      Clear
                    </button>
                  </div>
                )}

                {/* Tone */}
                <div style={card}>
                  <span style={lbl}>Tone</span>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {[
                      { id:"professional", emoji:"💼", label:"Professional", desc:"Formal and polished" },
                      { id:"confident",    emoji:"🎯", label:"Confident",    desc:"Direct and assertive" },
                      { id:"enthusiastic", emoji:"🚀", label:"Enthusiastic", desc:"Warm and energetic" },
                    ].map(t => (
                      <div key={t.id} onClick={() => setTone(t.id)}
                        style={{ flex:1, minWidth:140, padding:"12px 16px", borderRadius:10, border:`2px solid ${tone===t.id?"#7c3aed":"#e2e8f0"}`, background:tone===t.id?"#f5f3ff":"#fff", cursor:"pointer", transition:"all 0.15s" }}>
                        <div style={{ fontSize:18, marginBottom:3 }}>{t.emoji}</div>
                        <div style={{ fontWeight:700, fontSize:13, color:tone===t.id?"#5b21b6":"#0f172a" }}>{t.label}</div>
                        <div style={{ fontSize:11, color:"#64748b" }}>{t.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resume */}
                <div style={card}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={lbl}>Resume Text</span>
                    {resumeText && <span style={{ fontSize:11, color:"#059669", fontWeight:600 }}>✅ {resumeText.split(" ").filter(Boolean).length} words</span>}
                  </div>
                  <textarea value={resumeText} onChange={e => setResumeText(e.target.value)}
                    placeholder="Paste your full resume text here..."
                    style={{ width:"100%", height:200, padding:"12px 14px", border:`1.5px solid ${resumeText?"#a5b4fc":"#e2e8f0"}`, borderRadius:10, fontSize:13, fontFamily:"inherit", lineHeight:1.6, outline:"none", resize:"vertical", background:resumeText?"#fafaff":"#fff", boxSizing:"border-box", transition:"all 0.15s" }} />
                </div>

                {/* Job Description */}
                <div style={card}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={lbl}>Job Description</span>
                    {jdText && <span style={{ fontSize:11, color:"#2563eb", fontWeight:600 }}>🎯 {jdText.split(" ").filter(Boolean).length} words</span>}
                  </div>
                  <textarea value={jdText} onChange={e => setJdText(e.target.value)}
                    placeholder="Paste the full job description here..."
                    style={{ width:"100%", height:200, padding:"12px 14px", border:`1.5px solid ${jdText?"#bfdbfe":"#e2e8f0"}`, borderRadius:10, fontSize:13, fontFamily:"inherit", lineHeight:1.6, outline:"none", resize:"vertical", background:jdText?"#eff6ff":"#fff", boxSizing:"border-box", transition:"all 0.15s" }} />
                </div>

                {error && (
                  <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:10, padding:"12px 16px", marginBottom:16, fontSize:13, color:"#dc2626", display:"flex", gap:8 }}>
                    ⚠️ {error}
                  </div>
                )}

                <button onClick={handleGenerate} disabled={!resumeText.trim()||!jdText.trim()}
                  style={{ width:"100%", padding:"16px", background:(!resumeText.trim()||!jdText.trim())?"#e2e8f0":"linear-gradient(135deg,#7c3aed,#6d28d9)", color:(!resumeText.trim()||!jdText.trim())?"#94a3b8":"#fff", border:"none", borderRadius:12, fontWeight:700, fontSize:15, cursor:(!resumeText.trim()||!jdText.trim())?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:10, boxShadow:(!resumeText.trim()||!jdText.trim())?"none":"0 6px 20px rgba(124,58,237,0.3)", transition:"all 0.2s" }}>
                  ✉️ Generate Cover Letter
                </button>
                <p style={{ textAlign:"center", fontSize:12, color:"#94a3b8", marginTop:10 }}>
                  🔒 Saved to your account for 30 days · Re-download anytime
                </p>
              </>
            )}

            {/* GENERATING */}
            {pageStep === "generating" && (
              <div style={{ ...card, textAlign:"center", padding:"64px 40px", animation:"fadeIn 0.3s ease" }}>
                <div style={{ position:"relative", width:72, height:72, margin:"0 auto 24px" }}>
                  <div style={{ position:"absolute", inset:0, borderRadius:"50%", border:"4px solid #e2e8f0", borderTopColor:"#7c3aed", animation:"spin 0.8s linear infinite" }} />
                  <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", fontSize:28 }}>✉️</div>
                </div>
                <h3 style={{ fontSize:18, fontWeight:700, color:"#0f172a", marginBottom:8 }}>Writing your cover letter...</h3>
                <p style={{ fontSize:13, color:"#64748b", maxWidth:320, margin:"0 auto", lineHeight:1.7 }}>
                  Matching your experience to the job description and crafting a compelling letter.
                </p>
              </div>
            )}

            {/* RESULT */}
            {pageStep === "result" && letter && (
              <div style={{ animation:"fadeIn 0.4s ease" }}>

                {/* Action bar */}
                <div style={{ background:"#fff", border:"1.5px solid #ddd6fe", borderRadius:14, padding:"14px 20px", marginBottom:20, display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", boxShadow:"0 2px 8px rgba(124,58,237,0.08)" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#059669", display:"flex", alignItems:"center", gap:8 }}>
                      ✓ Cover letter ready
                      {saving && <span style={{ fontSize:11, color:"#94a3b8", fontWeight:400 }}>Saving...</span>}
                      {savedDocId && !saving && (
                        <span style={{ fontSize:11, background:"#f5f3ff", color:"#7c3aed", padding:"1px 8px", borderRadius:20, fontWeight:600 }}>
                          💾 Saved · 30 days
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                      Tailored to the job using your real experience
                    </div>
                  </div>
                  <DownloadButtons letterData={letter} />
                </div>

                {/* Subject line */}
                {letter.subject && (
                  <div style={{ background:"#f5f3ff", border:"1px solid #ddd6fe", borderRadius:10, padding:"10px 16px", marginBottom:16, fontSize:13 }}>
                    <span style={{ fontWeight:700, color:"#5b21b6" }}>📧 Subject: </span>
                    <span style={{ color:"#1e1b4b" }}>{letter.subject}</span>
                  </div>
                )}

                {/* Letter preview */}
                <LetterPreview letterData={letter} />

                {/* Actions */}
                <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                  <button onClick={() => setActiveTab("history")}
                    style={{ padding:"10px 22px", background:"#f5f3ff", color:"#7c3aed", border:"1.5px solid #ddd6fe", borderRadius:10, fontWeight:600, fontSize:13, cursor:"pointer" }}>
                    📁 View Saved Letters
                  </button>
                  <button onClick={handleReset}
                    style={{ padding:"10px 22px", background:"transparent", color:"#64748b", border:"1.5px solid #e2e8f0", borderRadius:10, fontWeight:600, fontSize:13, cursor:"pointer" }}>
                    ← Generate Another
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
